import React, { lazy, Suspense } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";

import { useAuth } from "../../contexts/AuthContext";
import { FAIL_CLOSED, featureForPath, isRedirect } from "../../access/registry";
import StaffGuard from "./StaffGuard";

// Its own chunk: only a signed-out visit to a gated URL ever needs it, and it
// pulls in the app shell and top bar, which no marketing page does.
const GateShell = lazy(() => import("./GateShell"));

export const GateSpinner = () => (
  <div
    className="flex min-h-screen items-center justify-center"
    role="status"
    aria-label="Loading"
  >
    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" aria-hidden="true" />
  </div>
);

const Shell = (props) => (
  <Suspense fallback={<GateSpinner />}>
    <GateShell {...props} />
  </Suspense>
);

/**
 * The one route gate: a layout route around every page in App.js, deciding
 * from the feature registry whether the page renders.
 *
 * - A redirect source (`/my-properties`, `/settings`, `/app/v2`) renders, so
 *   its `<Navigate>` runs; the target is classified on the next render. Never
 *   a shell at the old path.
 * - An open feature renders at once and never waits on Firebase.
 * - Anything else — an auth-required feature, or a path the registry does not
 *   know (fail closed) — waits for the session to restore, then renders for a
 *   signed-in user and shows the sign-in shell to a signed-out one.
 * - A `staff` guard (`/app/staff…`) asks the API through `StaffGuard`: staff
 *   get the page, everyone else a "no access" card. The staff page is still
 *   only the `<Outlet />` child, so its chunk loads for staff alone.
 * - A guard this gate does not know is refused, not waved through.
 *
 * It classifies by path only. A gated surface opened by a URL parameter (the
 * console's `?all=1`) is gated where it renders — see `GatedSection`.
 */
const KNOWN_GUARDS = ["staff"];

const AccessGate = () => {
  const { pathname, search } = useLocation();
  const { status } = useAuth();

  if (isRedirect(pathname)) return <Outlet />;
  const feature = featureForPath(pathname) ?? FAIL_CLOSED;

  if (feature.access === "open") return <Outlet />;
  if (status === "loading") return <GateSpinner />;
  if (status !== "signed-in") {
    return <Shell feature={feature} pathname={pathname} search={search} />;
  }
  const guards = feature.guards || [];
  if (guards.some((g) => !KNOWN_GUARDS.includes(g))) {
    return <Shell feature={feature} pathname={pathname} search={search} noAccess />;
  }
  if (guards.includes("staff")) {
    return (
      <StaffGuard
        spinner={<GateSpinner />}
        renderShell={(extra) => (
          <Shell feature={feature} pathname={pathname} search={search} {...extra} />
        )}
      >
        <Outlet />
      </StaffGuard>
    );
  }
  return <Outlet />;
};

export default AccessGate;
